import React, { useEffect, useState } from "react";
import {
  createVocabulary,
  updateVocabulary,
} from "./adminVocabularyService";
import "../../announcement/announcement.css";
import "../../styles/content.css";

const difficultyOptions = [
  "Beginner",
  "Elementary",
  "Intermediate",
  "Upper Intermediate",
  "Advanced",
  "Expert",
];

const partOfSpeechOptions = [
  "Noun",
  "Pronoun",
  "Verb",
  "Adjective",
  "Adverb",
  "Preposition",
  "Conjunction",
  "Interjection",
  "Article",
  "Determiner",
  "Phrasal Verb",
  "Idiom",
];

const toList = (str) =>
  str
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

const toStr = (arr) => (Array.isArray(arr) ? arr.join("\n") : arr || "");

const AdminVocabularyForm = ({ vocabulary, onSaved, onCancel }) => {
  const isEdit = !!vocabulary;

  const [form, setForm] = useState({
    word: "",
    pronunciation: "",
    partOfSpeech: "",
    definition: "",
    examples: "",
    difficulty: "",
    synonyms: "",
    antonyms: "",
    youtubeVideoLink: "",
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (vocabulary) {
      setForm({
        word: vocabulary.word || "",
        pronunciation: vocabulary.pronunciation || "",
        partOfSpeech: vocabulary.partOfSpeech || "",
        definition: vocabulary.definition || "",
        examples: toStr(vocabulary.examples),
        difficulty: vocabulary.difficulty || "",
        synonyms: toStr(vocabulary.synonyms),
        antonyms: toStr(vocabulary.antonyms),
        youtubeVideoLink: vocabulary.youtubeVideoLink || "",
      });
    }
  }, [vocabulary]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.word || !form.definition || !form.difficulty) {
      alert("Word, Definition, and Difficulty are required");
      return;
    }

    const payload = {
      word: form.word,
      pronunciation: form.pronunciation,
      partOfSpeech: form.partOfSpeech,
      definition: form.definition,
      examples: toList(form.examples),
      difficulty: form.difficulty,
      synonyms: toList(form.synonyms),
      antonyms: toList(form.antonyms),
      ...(form.youtubeVideoLink && { youtubeVideoLink: form.youtubeVideoLink }),
    };

    setLoading(true);
    const res = isEdit
      ? await updateVocabulary(vocabulary.id, payload)
      : await createVocabulary(payload);

    if (res.status === 200 || res.status === 201) {
      onSaved();
    } else {
      alert(res.message || "Operation failed");
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <div className="form-header">
            <h2>{isEdit ? "Edit Vocabulary" : "Create Vocabulary"}</h2>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Word *</label>
              <input
                name="word"
                placeholder="Enter word"
                value={form.word}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Pronunciation</label>
              <input
                name="pronunciation"
                placeholder="e.g. /əˈbʌndəns/"
                value={form.pronunciation}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Part of Speech</label>
              <select
                name="partOfSpeech"
                value={form.partOfSpeech}
                onChange={handleChange}
              >
                <option value="">Select Part of Speech</option>
                {partOfSpeechOptions.map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Difficulty *</label>
              <select
                name="difficulty"
                value={form.difficulty}
                onChange={handleChange}
              >
                <option value="">Select Difficulty</option>
                {difficultyOptions.map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label>Definition *</label>
              <textarea
                name="definition"
                placeholder="Enter definition"
                value={form.definition}
                onChange={handleChange}
                rows={2}
              />
            </div>

            <div className="form-group full-width">
              <label>Examples (one per line)</label>
              <textarea
                name="examples"
                placeholder={"The room filled with laughter.\nLaughter is the best medicine."}
                value={form.examples}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Synonyms (one per line)</label>
              <textarea
                name="synonyms"
                placeholder={"chuckling\ngiggling\nmirth"}
                value={form.synonyms}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Antonyms (one per line)</label>
              <textarea
                name="antonyms"
                placeholder={"crying\nweeping\nsorrow"}
                value={form.antonyms}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="form-group full-width">
              <label>YouTube Video Link (optional)</label>
              <input
                name="youtubeVideoLink"
                placeholder="https://youtube.com/..."
                value={form.youtubeVideoLink}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              className="btn primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
            <button
              className="btn secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVocabularyForm;
